import { IconVariant } from "@/utils/fontAwesomeUtils";
import url from "url";

export function trimString(input: string, chars: number): string {
	if (!input) return input;

	let string = "";
	for (let i = 0; i < chars && i < input.length; i++) {
		string += input[i];
	}
	return string;
}

export function extractHostname(website: string): string {
	const hostname = url.parse(website).hostname;
	if (hostname) {
		return hostname;
	}
	return website;
}

export interface BackgroundTab {
	tabUrl: string;
	favIcon: string;
	title: string;
	tab_id: string;
}

export interface WindowData {
	action: string;
	background_tabs?: BackgroundTab[];
	chrome_id: string;
	chrome_window_id: string;
	email: string;
	expiry: number;
	favicon: string;
	platform_type: string;
	screenshot: string;
	tab_id: string;
	teacherIds: string[];
	title: string;
	url: string;
	restricted?: Restricted;
}

export interface Restricted {
	domains?: string[];
	apps?: string[];
}

export interface RestrictedModalItem {
	tab_id?: string;
	favIcon?: string;
	favicon?: string;
	tabUrl: string;
	title: string;
	//canApplyRule determines if we can enable the rules button besides the app/website
	//At present, domains can have rules but apps cannot.
	canApplyRule: boolean;
}

export function isCompanionMode(windowData: WindowData): boolean {
	return !!windowData.platform_type && windowData.platform_type === "companion";
}

export function platformIcon(windowData: WindowData): { iconName: string; variant: IconVariant } {
	if (!windowData) return { iconName: "fa-laptop", variant: "solid" };

	if (
		(windowData.chrome_id && windowData.chrome_id.length > 0 && windowData.tab_id && windowData.tab_id.length > 0) || // This line can be removed after EDU-4969
		(windowData.platform_type && windowData.platform_type === "chrome")
	)
		return { iconName: "fa-chrome", variant: "brands" };

	if (windowData.platform_type && windowData.platform_type === "windows") return { iconName: "fa-windows", variant: "brands" };

	if (windowData.platform_type && windowData.platform_type === "macos") return { iconName: "fa-apple", variant: "brands" };

	return { iconName: "fa-laptop", variant: "solid" };
}

/**
 * Returns true if the windowData's platform_type supports chat (chrome or companion)
 */

const isRestrictedDomainModalItem = (data: unknown): data is RestrictedModalItem =>
	typeof data === "object" && data !== null && "tabUrl" in data;
const isRestrictedAppModalItem = (data: unknown): data is RestrictedModalItem =>
	typeof data === "object" && data !== null && "title" in data;

/**
 * Returns true if either of the arrays in windowData.restricted object have length greater than 0
 * @param windowData
 * @returns boolean
 */
export const isBeingRestricted = (windowData: WindowData): boolean => {
	if (!windowData.restricted) return false;

	if (windowData.restricted.domains && windowData.restricted.domains.length > 0) return true;
	if (windowData.restricted.apps && windowData.restricted.apps.length > 0) return true;

	return false;
};

export const restrictedModalItems = (windowData: WindowData): Array<RestrictedModalItem> => {
	const items: Array<RestrictedModalItem> = [];

	if (!windowData) return items;

	//The active tab will be added to the item
	//We will later parse background_tabs
	const isRestricted = windowData.restricted?.domains?.some((domain) => {
		const strippedDomain = strippedWildcardDomain(domain);
		if (strippedDomain) {
			return windowData.url.includes(strippedDomain);
		}
		return false;
	});
	if (isRestricted) {
		items.push({
			tab_id: windowData.tab_id,
			favIcon: windowData.favicon,
			tabUrl: windowData.url,
			title: windowData.title,
			canApplyRule: true,
		});
	}

	// we need to check if the background tabs exists before proceeding further
	if (windowData.background_tabs) {
		for (const [, tab] of Object.entries(windowData.background_tabs)) {
			if (isRestrictedDomainModalItem(tab)) {
				const isRestricted = windowData.restricted?.domains?.some((domain) => {
					const strippedDomain = strippedWildcardDomain(domain);
					if (strippedDomain) {
						return tab.tabUrl.includes(strippedDomain);
					}
					return false;
				});
				if (isRestricted) {
					tab.canApplyRule = true;
					items.push(tab);
				}
			}
		}
	}

	//now we add the restricted domains in the modal items
	windowData.restricted?.apps?.forEach((appName: string) => {
		const item = { title: appName };
		if (isRestrictedAppModalItem(item)) {
			items.push(item);
		}
	});

	return items;
};

// Login to strip the wildcard domain for example *.nab.com.au will be nab.com.au
const strippedWildcardDomain = (domain: string): string | undefined => domain.split("*.").pop();
